"use client";

import { Button } from "@/components/ui/button";
import { Icon } from "@/components/ui/icon";
import { useTranslations } from "next-intl";
import { useSearchParams } from "next/navigation";
import { useCallback, useMemo } from "react";
import { setLastLoginMethod } from "@/components/auth/common/last-login-method";

export type SocialAuthButtonsProps = {
  standalone?: boolean;
};

export function SocialAuthButtons({ standalone }: SocialAuthButtonsProps) {
  const t = useTranslations();

  const searchParams = useSearchParams();
  const redirect = standalone
    ? (searchParams.get("redirect") ?? undefined)
    : typeof window === "undefined"
      ? undefined
      : window.location.pathname + window.location.search;

  const googleUrl = useMemo(() => {
    if (!redirect) {
      return "/api/oauth/google";
    }

    const params = new URLSearchParams({ redirect });
    return `/api/oauth/google?${params.toString()}`;
  }, [standalone, redirect]);

  const facebookUrl = useMemo(() => {
    if (!redirect) {
      return "/api/oauth/facebook";
    }

    const params = new URLSearchParams({ redirect });
    return `/api/oauth/facebook?${params.toString()}`;
  }, [standalone, redirect]);

  const handleGoogleClick = useCallback(() => {
    setLastLoginMethod("google");
  }, []);

  const handleFacebookClick = useCallback(() => {
    setLastLoginMethod("facebook");
  }, []);

  return (
    <div className="space-y-4">
      <Button type="button" variant="outline" className="w-full" asChild>
        <a href={googleUrl} onClick={handleGoogleClick}>
          <Icon icon="Google" /> {t("saas.auth.social.buttons.google")}
        </a>
      </Button>

      <Button type="button" variant="outline" className="w-full" asChild>
        <a href={facebookUrl} onClick={handleFacebookClick}>
          <Icon icon="Facebook" /> {t("saas.auth.social.buttons.facebook")}
        </a>
      </Button>
    </div>
  );
}

SocialAuthButtons.displayName = "SocialAuthButtons";
